import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import { hasActualTextContent } from '../utils/sanitize';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

interface DuplicateIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  incident: any;
  onSuccess?: (newIncidentNumber: string) => void; // Optional callback when incident is duplicated
}

// Impact options for Combobox (backend expects: high_1, medium_2, low_3)
const impactOptions = [
  { id: 'high_1', name: '1 - High' },
  { id: 'medium_2', name: '2 - Medium' },
  { id: 'low_3', name: '3 - Low' }
];

// Urgency options for Combobox (backend expects: high_1, medium_2, low_3)
const urgencyOptions = [
  { id: 'high_1', name: '1 - High' },
  { id: 'medium_2', name: '2 - Medium' },
  { id: 'low_3', name: '3 - Low' }
];

// Channel options (must match CaseChannel enum: web, phone, email, alert)
const channelOptions = [
  { id: 'web', name: 'Web' },
  { id: 'phone', name: 'Phone' },
  { id: 'email', name: 'Email' },
  { id: 'alert', name: 'Alert' }
];

// Category options
const categoryOptions = [
  { id: 'issue', name: 'Issue' },
  { id: 'request', name: 'Request' },
  { id: 'inquiry', name: 'Inquiry' }
];

// Priority calculation based on Impact and Urgency (moved outside component for stability)
const calculatePriority = (impact: string, urgency: string): string => {
  const matrix: Record<string, Record<string, string>> = {
    'high_1': { 'high_1': 'critical_1', 'medium_2': 'high_2', 'low_3': 'moderate_3' },
    'medium_2': { 'high_1': 'high_2', 'medium_2': 'moderate_3', 'low_3': 'low_4' },
    'low_3': { 'high_1': 'moderate_3', 'medium_2': 'low_4', 'low_3': 'planning_5' }
  };
  return matrix[impact]?.[urgency] || 'moderate_3';
};

const DuplicateIncidentModal: React.FC<DuplicateIncidentModalProps> = ({
  isOpen,
  onClose,
  incident,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const isRichTextEmpty = (content: string): boolean => !hasActualTextContent(content);

  const [formData, setFormData] = useState({
    impact: 'low_3',
    urgency: 'low_3',
    priority: 'planning_5',
    channel: 'web',
    category: 'issue',
    customerId: '',
    engagementId: '',
    contact: '',
    servicePlanId: '',
    resourceId: '',
    resourceIdValue: '',
    otherResourceNames: '',
    assignmentGroupId: '',
    assignedTo: '',
    shortDescription: ''
  });
  const [isOtherResource, setIsOtherResource] = useState(false);

  // Search query states for Combobox fields
  const [impactQuery, setImpactQuery] = useState('');
  const [urgencyQuery, setUrgencyQuery] = useState('');
  const [channelQuery, setChannelQuery] = useState('');
  const [categoryQuery, setCategoryQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [servicePlanQuery, setServicePlanQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [groupQuery, setGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements'],
    queryFn: async () => {
      const response = await api.get('/engagements');
      return response.data;
    }
  });

  // Fetch assignment groups (only type 'assignment')
  const { data: groupsData } = useQuery({
    queryKey: ['groups', 'assignment'],
    queryFn: async () => {
      const response = await api.get('/groups?type=assignment');
      return response.data;
    }
  });

  // Fetch service plans based on engagement
  const { data: servicePlansData } = useQuery({
    queryKey: ['service-plans', formData.engagementId],
    queryFn: async () => {
      const response = await api.get(`/engagements/${formData.engagementId}/service-plans`);
      return response.data;
    },
    enabled: !!formData.engagementId
  });

  // Fetch resources based on engagement
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return [];
      const response = await api.get(`/resources?engagementId=${formData.engagementId}`);
      return Array.isArray(response.data) ? response.data : (response.data.resources || []);
    },
    enabled: !!formData.engagementId
  });

  // Fetch users based on assignment group
  const { data: usersData } = useQuery({
    queryKey: ['group-users', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      return fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
    },
    enabled: !!formData.assignmentGroupId
  });

  // Fetch contacts for Contact field (filtered by customer and engagement)
  const { data: contactsData } = useQuery({
    queryKey: ['contacts-dropdown', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (formData.engagementId) {
        const response = await api.get(`/contacts/dropdown?engagementId=${formData.engagementId}`);
        return response.data;
      } else if (formData.customerId) {
        const response = await api.get(`/contacts/dropdown?customerId=${formData.customerId}`);
        return response.data;
      }
      return { contacts: [] };
    },
    enabled: !!formData.customerId || !!formData.engagementId
  });

  // Fetch primary contact for auto-selection
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Auto-calculate priority when impact or urgency changes
  useEffect(() => {
    if (formData.impact && formData.urgency) {
      const calculatedPriority = calculatePriority(formData.impact, formData.urgency);
      setFormData(prev => ({ ...prev, priority: calculatedPriority }));
    }
  }, [formData.impact, formData.urgency]);

  // Update Resource ID value when resource changes
  useEffect(() => {
    if (formData.resourceId && resourcesData) {
      const resourcesArray = Array.isArray(resourcesData) ? resourcesData : [];
      const selectedResource = resourcesArray.find((r: any) => r.id === formData.resourceId);
      if (selectedResource) {
        setFormData(prev => ({ ...prev, resourceIdValue: selectedResource.resourceId || '' }));
      }
    }
  }, [formData.resourceId, resourcesData]);

  // Auto-select primary contact when customer/engagement changes
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contact) {
      setFormData(prev => ({
        ...prev,
        contact: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contact]);

  // Pre-fill form when incident data changes
  useEffect(() => {
    if (incident && isOpen) {
      // Check if incident has otherResourceNames (custom resources)
      const hasOtherResources = incident.otherResourceNames && incident.otherResourceNames.trim();
      setIsOtherResource(!!hasOtherResources);

      // Get impact and urgency values
      const impactValue = incident.impact || 'low_3';
      const urgencyValue = incident.urgency || 'low_3';
      // Calculate priority based on impact and urgency matrix (not from source incident)
      const calculatedPriority = calculatePriority(impactValue, urgencyValue);

      setFormData({
        impact: impactValue,
        urgency: urgencyValue,
        priority: calculatedPriority,
        channel: incident.channel || 'web',
        category: incident.category || 'issue',
        customerId: incident.customerId || incident.Customer?.id || '',
        engagementId: incident.engagementId || incident.Engagement?.id || '',
        contact: incident.contact?.id || '',
        servicePlanId: incident.servicePlanId || incident.ServicePlan?.id || '',
        resourceId: hasOtherResources ? '' : (incident.resourceId || incident.Resource?.id || ''),
        resourceIdValue: hasOtherResources ? '' : (incident.resourceIdValue || incident.Resource?.resourceId || ''),
        otherResourceNames: incident.otherResourceNames || '',
        assignmentGroupId: incident.assignmentGroupId || incident.assignmentGroup?.id || '',
        assignedTo: incident.assignedTo?.id || '',
        shortDescription: incident.shortDescription ? `Copy of ${incident.shortDescription}` : ''
      });
      setErrorMessage(null);
      setValidationErrors({});
    }
  }, [incident, isOpen]);

  const duplicateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/incidents', data);
      return response.data;
    },
    onSuccess: async (data) => {
      try {
        await api.post(`/cases/${data.incident.id}/related-records`, {
          relationships: [{
            targetId: incident.id,
            relationshipType: 'RELATED_TO'
          }],
          source: 'duplicate'
        });
      } catch (error) {
        console.error('Failed to create related record:', error);
      }

      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['related-records', incident.id] });
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data.incident.number || data.incident.id);
      }
      
      onClose();
      navigate(`/incidents/${data.incident.id}`);
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || 'Failed to duplicate incident';
      setErrorMessage(errorMsg);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const errors: Record<string, string> = {};

    if (!formData.customerId) {
      errors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      errors.engagementId = 'Engagement is required';
    }
    // Resource is required: either resourceId OR otherResourceNames
    if (!formData.resourceId && !(isOtherResource && formData.otherResourceNames.trim())) {
      errors.resourceId = 'Resource is required';
    }
    if (!formData.assignmentGroupId) {
      errors.assignmentGroupId = 'Assignment Group is required';
    }
    if (isRichTextEmpty(formData.shortDescription)) {
      errors.shortDescription = 'Short Description is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setValidationErrors({});
    setErrorMessage(null);

    const payload: any = {
      shortDescription: formData.shortDescription,
      impact: formData.impact,
      urgency: formData.urgency,
      priority: formData.priority,
      channel: formData.channel,
      category: formData.category,
      customerId: formData.customerId,
      engagementId: formData.engagementId,
      callerId: formData.contact || undefined,
      servicePlanId: formData.servicePlanId || undefined,
      assignmentGroupId: formData.assignmentGroupId || undefined,
      assignedTo: formData.assignedTo,
      // Track the source incident for activity logging
      duplicatedFromId: incident.id,
      duplicatedFromNumber: incident.number
    };

    // Handle resource: either resourceId OR otherResourceNames (mutually exclusive)
    if (isOtherResource && formData.otherResourceNames.trim()) {
      payload.otherResourceNames = formData.otherResourceNames.trim();
    } else if (formData.resourceId) {
      payload.resourceId = formData.resourceId;
      payload.resourceIdValue = formData.resourceIdValue || undefined;
    }

    duplicateMutation.mutate(payload);
  };

  const customers = customersData?.customers || [];
  const allEngagements = engagementsData?.engagements || [];
  const engagements = formData.customerId
    ? allEngagements.filter((e: any) => e.Customer?.id === formData.customerId)
    : allEngagements;
  const groups = (groupsData?.groups || []).filter((g: any) => g.type === 'assignment');
  const servicePlans = servicePlansData?.plans || [];
  const resources = Array.isArray(resourcesData) ? resourcesData : [];
  const groupUsers = usersData || [];
  const contacts = contactsData?.contacts || [];

  // Filtered lists for Combobox search
  const filteredImpactOptions = impactQuery === ''
    ? impactOptions
    : impactOptions.filter((o) => o.name.toLowerCase().includes(impactQuery.toLowerCase()));

  const filteredUrgencyOptions = urgencyQuery === ''
    ? urgencyOptions
    : urgencyOptions.filter((o) => o.name.toLowerCase().includes(urgencyQuery.toLowerCase()));

  const filteredChannelOptions = channelQuery === ''
    ? channelOptions
    : channelOptions.filter((o) => o.name.toLowerCase().includes(channelQuery.toLowerCase()));

  const filteredCategoryOptions = categoryQuery === ''
    ? categoryOptions
    : categoryOptions.filter((o) => o.name.toLowerCase().includes(categoryQuery.toLowerCase()));

  const filteredCustomers = customerQuery === ''
    ? customers
    : customers.filter((c: any) => c.accountName?.toLowerCase().includes(customerQuery.toLowerCase()));

  const filteredEngagements = engagementQuery === ''
    ? engagements
    : engagements.filter((e: any) => {
        const displayName = `${e.Customer?.accountName || ''} - ${e.name}`;
        return displayName.toLowerCase().includes(engagementQuery.toLowerCase());
      });

  const filteredContacts = contactQuery === ''
    ? contacts
    : contacts.filter((c: any) => {
        const displayName = c.firstName && c.lastName ? `${c.firstName} ${c.lastName} - ${c.email}` : c.email;
        return displayName.toLowerCase().includes(contactQuery.toLowerCase());
      });

  const filteredServicePlans = servicePlanQuery === ''
    ? servicePlans
    : servicePlans.filter((p: any) => (p.name || p.planName)?.toLowerCase().includes(servicePlanQuery.toLowerCase()));

  const filteredResources = resourceQuery === ''
    ? resources
    : resources.filter((r: any) => {
        const displayName = r.name || r.resourceId || '';
        return displayName.toLowerCase().includes(resourceQuery.toLowerCase());
      });

  const filteredGroups = groupQuery === ''
    ? groups
    : groups.filter((g: any) => g.name?.toLowerCase().includes(groupQuery.toLowerCase()));

  const filteredGroupUsers = assignedToQuery === ''
    ? groupUsers
    : groupUsers.filter((u: any) => {
        const displayName = u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email;
        return displayName.toLowerCase().includes(assignedToQuery.toLowerCase());
      });

  // Helper functions to get selected items
  const getSelectedImpact = () => impactOptions.find((o) => o.id === formData.impact);
  const getSelectedUrgency = () => urgencyOptions.find((o) => o.id === formData.urgency);
  const getSelectedChannel = () => channelOptions.find((o) => o.id === formData.channel);
  const getSelectedCategory = () => categoryOptions.find((o) => o.id === formData.category);
  const getSelectedCustomer = () => {
    const found = customers.find((c: any) => c.id === formData.customerId);
    if (found) return found;
    // Fallback to incident's Customer data
    if (formData.customerId && incident?.Customer?.id === formData.customerId) {
      return incident.Customer;
    }
    return null;
  };
  const getSelectedEngagement = () => {
    const found = engagements.find((e: any) => e.id === formData.engagementId);
    if (found) return found;
    // Fallback to incident's Engagement data
    if (formData.engagementId && incident?.Engagement?.id === formData.engagementId) {
      return incident.Engagement;
    }
    return null;
  };
  const getSelectedContact = () => {
    const found = contacts.find((c: any) => c.id === formData.contact);
    if (found) return found;
    // Fallback to incident's contact data
    if (formData.contact && incident?.contact?.id === formData.contact) {
      return { ...incident.contact, name: `${incident.contact.firstName || ''} ${incident.contact.lastName || ''}`.trim() || incident.contact.email };
    }
    return null;
  };
  const getSelectedServicePlan = () => {
    const found = servicePlans.find((p: any) => p.id === formData.servicePlanId);
    if (found) return found;
    // Fallback to incident's ServicePlan data
    if (formData.servicePlanId && incident?.ServicePlan?.id === formData.servicePlanId) {
      return incident.ServicePlan;
    }
    return null;
  };
  const getSelectedResource = () => {
    // First try to find in loaded resources
    const foundResource = resources.find((r: any) => r.id === formData.resourceId);
    if (foundResource) return foundResource;
    // Fallback to original incident's Resource data if resources haven't loaded yet
    if (formData.resourceId && incident?.Resource?.id === formData.resourceId) {
      return incident.Resource;
    }
    return null;
  };
  const getSelectedGroup = () => {
    const found = groups.find((g: any) => g.id === formData.assignmentGroupId);
    if (found) return found;
    // Fallback to incident's assignmentGroup data
    if (formData.assignmentGroupId && incident?.assignmentGroup?.id === formData.assignmentGroupId) {
      return incident.assignmentGroup;
    }
    return null;
  };
  const getSelectedUser = () => {
    const found = groupUsers.find((u: any) => u.id === formData.assignedTo);
    if (found) return found;
    // Fallback to incident's assignedTo data
    if (formData.assignedTo && incident?.assignedTo?.id === formData.assignedTo) {
      return incident.assignedTo;
    }
    return null;
  };

  // Helper function to get priority label
  const getPriorityLabel = (priority: string): string => {
    const labels: Record<string, string> = {
      'critical_1': '1 - Critical',
      'high_2': '2 - High',
      'moderate_3': '3 - Moderate',
      'low_4': '4 - Low',
      'planning_5': '5 - Planning'
    };
    return labels[priority] || '3 - Moderate';
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Duplicate Incident
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
                  {errorMessage && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">{errorMessage}</div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Row 1: Impact and Urgency */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Impact */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Impact
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedImpact() || null}
                            onChange={(option: any) => {
                              setFormData({ ...formData, impact: option?.id || 'low_3' });
                              setImpactQuery('');
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                displayValue={(option: any) => option?.name || ''}
                                onChange={(e) => setImpactQuery(e.target.value)}
                                placeholder="Select impact..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredImpactOptions.map((option) => (
                                  <Combobox.Option
                                    key={option.id}
                                    value={option}
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {option.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                        </div>
                      </div>

                      {/* Urgency */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Urgency
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedUrgency() || null}
                            onChange={(option: any) => {
                              setFormData({ ...formData, urgency: option?.id || 'low_3' });
                              setUrgencyQuery('');
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                displayValue={(option: any) => option?.name || ''}
                                onChange={(e) => setUrgencyQuery(e.target.value)}
                                placeholder="Select urgency..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredUrgencyOptions.map((option) => (
                                  <Combobox.Option
                                    key={option.id}
                                    value={option}
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {option.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Priority and State */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Priority (readonly) */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Priority
                        </label>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={getPriorityLabel(formData.priority)}
                            disabled
                            className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                          />
                        </div>
                      </div>

                      {/* State (readonly) */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          State
                        </label>
                        <div className="flex-1">
                          <input
                            type="text"
                            value="New"
                            disabled
                            className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Channel and Category */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Channel */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Channel
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedChannel() || null}
                            onChange={(option: any) => {
                              setFormData({ ...formData, channel: option?.id || 'web' });
                              setChannelQuery('');
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                displayValue={(option: any) => option?.name || ''}
                                onChange={(e) => setChannelQuery(e.target.value)}
                                placeholder="Select channel..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredChannelOptions.map((option) => (
                                  <Combobox.Option
                                    key={option.id}
                                    value={option}
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {option.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                        </div>
                      </div>

                      {/* Category */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Category
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedCategory() || null}
                            onChange={(option: any) => {
                              setFormData({ ...formData, category: option?.id || 'issue' });
                              setCategoryQuery('');
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                displayValue={(option: any) => option?.name || ''}
                                onChange={(e) => setCategoryQuery(e.target.value)}
                                placeholder="Select category..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredCategoryOptions.map((option) => (
                                  <Combobox.Option
                                    key={option.id}
                                    value={option}
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {option.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                        </div>
                      </div>
                    </div>

                    {/* Row 4: Customer and Engagement */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Customer */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Customer <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedCustomer() || null}
                            onChange={(customer: any) => {
                              setFormData({ ...formData, customerId: customer?.id || '', engagementId: '', servicePlanId: '', resourceId: '', resourceIdValue: '', otherResourceNames: '', contact: '' });
                              setCustomerQuery('');
                              setIsOtherResource(false);
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full rounded-md border ${validationErrors.customerId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                displayValue={(customer: any) => customer?.accountName || ''}
                                onChange={(e) => setCustomerQuery(e.target.value)}
                                placeholder="Select Customer"
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredCustomers.length === 0 && customerQuery !== '' ? (
                                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">No customers found.</div>
                                ) : (
                                  filteredCustomers.map((customer: any) => (
                                    <Combobox.Option
                                      key={customer.id}
                                      value={customer}
                                      className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {customer.accountName}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))
                                )}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                          {validationErrors.customerId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.customerId}</p>
                          )}
                        </div>
                      </div>

                      {/* Engagement */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Engagement <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {formData.customerId ? (
                            <Combobox
                              value={getSelectedEngagement() || null}
                              onChange={(engagement: any) => {
                                setFormData({ ...formData, engagementId: engagement?.id || '', servicePlanId: '', resourceId: '', resourceIdValue: '', otherResourceNames: '', contact: '' });
                                setEngagementQuery('');
                                setIsOtherResource(false);
                              }}
                            >
                              <div className="relative">
                                <Combobox.Input
                                  className={`block w-full rounded-md border ${validationErrors.engagementId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                  displayValue={(engagement: any) => engagement ? `${engagement.Customer?.accountName || ''} - ${engagement.name}` : ''}
                                  onChange={(e) => setEngagementQuery(e.target.value)}
                                  placeholder="Select engagement..."
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredEngagements.length === 0 && engagementQuery !== '' ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">No engagements found.</div>
                                  ) : (
                                    filteredEngagements.map((engagement: any) => (
                                      <Combobox.Option
                                        key={engagement.id}
                                        value={engagement}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                        }
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {engagement.Customer?.accountName || ''} - {engagement.name}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </div>
                            </Combobox>
                          ) : (
                            <input
                              type="text"
                              disabled
                              value=""
                              placeholder="Select Customer first"
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            />
                          )}
                          {validationErrors.engagementId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.engagementId}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 5: Contact and Service Plan */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Contact */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Contact
                        </label>
                        <div className="flex-1">
                          {formData.engagementId ? (
                            <Combobox
                              value={getSelectedContact() || null}
                              onChange={(contact: any) => {
                                setFormData({ ...formData, contact: contact?.id || '' });
                                setContactQuery('');
                              }}
                            >
                              <div className="relative">
                                <Combobox.Input
                                  className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                  displayValue={(contact: any) => contact?.name || ''}
                                  onChange={(e) => setContactQuery(e.target.value)}
                                  placeholder="Select customer first"
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredContacts.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">No contacts available.</div>
                                  ) : (
                                    filteredContacts.map((contact: any) => (
                                      <Combobox.Option
                                        key={contact.id}
                                        value={contact}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                        }
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {contact.name}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </div>
                            </Combobox>
                          ) : (
                            <input
                              type="text"
                              disabled
                              value=""
                              placeholder="Select engagement first"
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            />
                          )}
                        </div>
                      </div>

                      {/* Service Plan */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Service Plan
                        </label>
                        <div className="flex-1">
                          {formData.engagementId ? (
                            <Combobox
                              value={getSelectedServicePlan() || null}
                              onChange={(plan: any) => {
                                setFormData({ ...formData, servicePlanId: plan?.id || '' });
                                setServicePlanQuery('');
                              }}
                            >
                              <div className="relative">
                                <Combobox.Input
                                  className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                  displayValue={(plan: any) => plan?.name || plan?.planName || ''}
                                  onChange={(e) => setServicePlanQuery(e.target.value)}
                                  placeholder="Select Engagement first"
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredServicePlans.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">No service plans available.</div>
                                  ) : (
                                    filteredServicePlans.map((plan: any) => (
                                      <Combobox.Option
                                        key={plan.id}
                                        value={plan}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                        }
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {plan.name || plan.planName}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </div>
                            </Combobox>
                          ) : (
                            <input
                              type="text"
                              disabled
                              value=""
                              placeholder="Select Engagement first"
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 6: Resource and Resource ID */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Resource */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Resource <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {formData.engagementId ? (
                            <Combobox
                              value={isOtherResource ? '__OTHER__' : (getSelectedResource() || null)}
                              onChange={(value: any) => {
                                if (value === '__OTHER__') {
                                  // User selected "Others"
                                  setIsOtherResource(true);
                                  setFormData({ ...formData, resourceId: '', resourceIdValue: '', otherResourceNames: '' });
                                } else {
                                  // User selected a normal resource
                                  setIsOtherResource(false);
                                  setFormData({ ...formData, resourceId: value?.id || '', otherResourceNames: '' });
                                }
                                setResourceQuery('');
                              }}
                            >
                              <div className="relative">
                                <Combobox.Input
                                  className={`block w-full rounded-md border ${validationErrors.resourceId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                  displayValue={(value: any) => {
                                    if (isOtherResource || value === '__OTHER__') return 'Others (specify custom resources)';
                                    return value?.name || value?.resourceId || '';
                                  }}
                                  onChange={(e) => setResourceQuery(e.target.value)}
                                  placeholder="Search resource..."
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredResources.length === 0 && resourceQuery !== '' ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">No resources found.</div>
                                  ) : (
                                    filteredResources.map((resource: any) => (
                                      <Combobox.Option
                                        key={resource.id}
                                        value={resource}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                        }
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {resource.name || resource.resourceId}
                                            </span>
                                            {selected && !isOtherResource && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                  {/* Others option - always shown at the end */}
                                  <Combobox.Option
                                    key="__OTHER__"
                                    value="__OTHER__"
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-10 pr-4 border-t border-gray-200 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ active }) => (
                                      <>
                                        <span className={`block truncate ${isOtherResource ? 'font-semibold' : 'font-normal'}`}>
                                          Others (specify custom resources)
                                        </span>
                                        {isOtherResource && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                </Combobox.Options>
                              </div>
                            </Combobox>
                          ) : (
                            <input
                              type="text"
                              disabled
                              value=""
                              placeholder="Select Engagement first"
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            />
                          )}
                          {validationErrors.resourceId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.resourceId}</p>
                          )}
                        </div>
                      </div>

                      {/* Resource ID (readonly) or Other Resource Names input */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          {isOtherResource ? 'Resource Names' : 'Resource ID'}
                        </label>
                        <div className="flex-1">
                          {isOtherResource ? (
                            <div>
                              <input
                                type="text"
                                value={formData.otherResourceNames}
                                onChange={(e) => setFormData({ ...formData, otherResourceNames: e.target.value })}
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 px-3 sm:text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Enter resource names (comma separated)"
                              />
                              <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={formData.resourceIdValue}
                              disabled
                              placeholder="Select a resource to view Resource ID"
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 7: Assignment Group and Assigned To */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Assignment Group */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assignment Group <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedGroup() || null}
                            onChange={(group: any) => {
                              setFormData({ ...formData, assignmentGroupId: group?.id || '', assignedTo: '' });
                              setGroupQuery('');
                              if (validationErrors.assignmentGroupId) {
                                setValidationErrors(prev => ({ ...prev, assignmentGroupId: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full rounded-md border bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                  validationErrors.assignmentGroupId ? 'border-red-300' : 'border-gray-300'
                                }`}
                                displayValue={(group: any) => group?.name || ''}
                                onChange={(e) => setGroupQuery(e.target.value)}
                                placeholder="Select Assignment Group"
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredGroups.length === 0 ? (
                                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">No groups found.</div>
                                ) : (
                                  filteredGroups.map((group: any) => (
                                    <Combobox.Option
                                      key={group.id}
                                      value={group}
                                      className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {group.name}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))
                                )}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                          {validationErrors.assignmentGroupId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignmentGroupId}</p>
                          )}
                        </div>
                      </div>

                      {/* Assigned To */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assigned To
                        </label>
                        <div className="flex-1">
                          {formData.assignmentGroupId ? (
                            <Combobox
                              value={getSelectedUser() || null}
                              onChange={(user: any) => {
                                setFormData({ ...formData, assignedTo: user?.id || '' });
                                setAssignedToQuery('');
                              }}
                            >
                              <div className="relative">
                                <Combobox.Input
                                  className={`block w-full rounded-md border ${validationErrors.assignedTo ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                  displayValue={(user: any) => user ? (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email) : ''}
                                  onChange={(e) => setAssignedToQuery(e.target.value)}
                                  placeholder="Select Assignment Group first"
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredGroupUsers.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">No users found.</div>
                                  ) : (
                                    filteredGroupUsers.map((user: any) => (
                                      <Combobox.Option
                                        key={user.id}
                                        value={user}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                        }
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </div>
                            </Combobox>
                          ) : (
                            <input
                              type="text"
                              disabled
                              value=""
                              placeholder="Select Assignment Group first"
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            />
                          )}
                          {validationErrors.assignedTo && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignedTo}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 8: Short Description (full width with RichTextEditor) */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        <span className="text-red-500">*</span> Short Description
                      </label>
                      <div className="flex-1">
                        <div className={validationErrors.shortDescription ? 'border border-red-500 rounded-md' : ''}>
                          <RichTextEditor
                            value={formData.shortDescription}
                            onChange={(value) => {
                              setFormData({ ...formData, shortDescription: value });
                              if (validationErrors.shortDescription) {
                                setValidationErrors(prev => ({ ...prev, shortDescription: '' }));
                              }
                            }}
                            placeholder="Brief summary of the incident"
                            minHeight="120px"
                          />
                        </div>
                        {validationErrors.shortDescription && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.shortDescription}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={duplicateMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                    >
                      {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate Incident'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DuplicateIncidentModal;
